import React, { useState } from 'react';
import { Col, Form, Row, Switch } from 'antd';
import CustomInput from '@/components/CustomInput';
import CustomTitle from '@/components/CustomTitle';
import CommonFooter from '@/components/CommonFooter';
import type { VendorConnectionData } from '@/apis/vendorConnections';
import { buildSavePayload } from '../vendorFormat';
import type { VendorFormValues } from '../vendorFormat';
import type { SaveOutcome } from '../types';
import '../../Sensors/sensors.css';
import './vendorConnectionForm.css';

const inputProps = {
  bgColor: 'var(--light-gray)',
  textColor: 'var(--text-primary)',
  height: '36px',
  borderRadius: '8px',
  border: 'none',
} as const;

interface VendorConnectionFormProps {
  vendor: VendorConnectionData;
  saving: boolean;
  onSave: (
    payload: ReturnType<typeof buildSavePayload> & { enabled?: boolean }
  ) => Promise<SaveOutcome>;
  onCancel: () => void;
}

/**
 * The connect / edit drawer for one vendor. The fields come from the vendor's
 * own metadata. Saved values are shown as stored, secrets included, like the
 * other Transport providers; only what changed is sent.
 */
const VendorConnectionForm: React.FC<VendorConnectionFormProps> = ({
  vendor,
  saving,
  onSave,
  onCancel,
}) => {
  const [form] = Form.useForm<VendorFormValues>();
  const [error, setError] = useState<string | null>(null);
  const initial: VendorFormValues = {
    enabled: vendor.enabled,
  };
  for (const field of vendor.credential_fields) {
    initial[field.name] = field.value ?? '';
  }

  const handleFinish = async (values: VendorFormValues): Promise<void> => {
    setError(null);
    const payload: ReturnType<typeof buildSavePayload> & { enabled?: boolean } =
      buildSavePayload(vendor, values);
    if (vendor.connected && values.enabled !== vendor.enabled) {
      payload.enabled = Boolean(values.enabled);
    }
    if (
      vendor.connected &&
      !payload.credentials &&
      payload.enabled === undefined
    ) {
      onCancel(); // nothing changed
      return;
    }
    const outcome = await onSave(payload);
    if (!outcome.ok) setError(outcome.message ?? 'Failed to save.');
  };

  return (
    <Form
      form={form}
      initialValues={initial}
      onFinish={handleFinish}
      layout='vertical'
      preserve={false}
      className='sensor-credential-form'
    >
      <div className='custom-scrollbar sensor-credential-scroll'>
        <Row gutter={[16, 14]}>
          <Col span={24}>
            <CustomTitle
              text={`${vendor.connected ? 'Edit' : 'Connect'} ${vendor.display_name}`}
              fontSize={24}
              fontWeight='700'
              color='var(--text-primary)'
              marginBottom={0}
            />
          </Col>
          {vendor.credential_fields.map((field) => (
            <Col span={24} key={field.name}>
              <Form.Item
                label={
                  field.optional ? `${field.label} (optional)` : field.label
                }
                name={field.name}
                rules={
                  field.optional
                    ? []
                    : [
                        {
                          required: true,
                          whitespace: true,
                          message: `Enter the ${field.label.toLowerCase()}`,
                        },
                      ]
                }
              >
                <CustomInput autoComplete='off' {...inputProps} />
              </Form.Item>
            </Col>
          ))}
          {vendor.connected && (
            <Col span={24}>
              <Form.Item
                label='Enabled'
                name='enabled'
                valuePropName='checked'
                extra='Disabled vendors are not searched. Credentials stay saved.'
              >
                <Switch aria-label='Enabled' />
              </Form.Item>
            </Col>
          )}
          {error && (
            <Col span={24}>
              <div role='alert' className='vendor-connection-error'>
                {error}
              </div>
            </Col>
          )}
        </Row>
      </div>

      <div className='sensor-drawer-footer'>
        <CommonFooter
          text={saving ? 'Saving...' : 'Save'}
          onSave={() => form.submit()}
          handleCancel={onCancel}
        />
      </div>
    </Form>
  );
};

export default VendorConnectionForm;
